<template>
  <div class="max-w-3xl">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <slot name="title">
      <h1 font-slab text-xl sm:text-2xl text-brand-orange><NuxtLink to="/">What is my elevation?</NuxtLink></h1>
    </slot>
    <div space-y-10 mt-4>
      <!-- at least as tall as the loading placeholder, so the search box below stays put when the location message or
           the elevation replaces it -->
      <div space-y-3 :class="{ 'min-h-28': currentLocationSupported }">
        <!-- in place of the elevation when the device location can't be found -->
        <LocationProblem v-if="locationError" :error="locationError" subject="the elevation where you are" />
        <slot
          v-else
          name="elevation"
          :locationFormatted="locationFormatted"
          :loading="loading"
          :elevationSources="elevationSources"
          :elevationSourcesPending="elevationSourcesPending"
          :showElevationSource="showElevationSource"
        >
          <div space-y-4>
            <p v-if="!loading && !locationFormatted.elevation.value && elevationMessage" text-lg text-neutral-600>
              {{ elevationMessage }}
            </p>
            <template v-else-if="loading || !locationFormatted.elevation.value">
              <p h-18 w-40 bg-stone-50 rounded animate-pulse :class="{ invisible: !loading }"></p>
              <div
                v-if="currentLocationSupported"
                h-6
                w-60
                bg-stone-100
                rounded
                animate-pulse
                :class="{ invisible: !loading }"
              ></div>
            </template>
            <template v-else-if="locationFormatted.elevation.value">
              <p class="text-6xl sm:text-7xl font-bold tabular-nums" v-if="locationFormatted.elevation.value">
                {{ locationFormatted.elevation.value }} {{ locationFormatted.elevationUnits.value.name }}
              </p>
              <p
                font-mono
                text-neutral-500
                tabular-nums
                space-x-2
                v-if="locationFormatted.elevation.value && currentLocationSupported"
              >
                <span v-if="locationFormatted.accuracy.value">
                  ±{{ locationFormatted.accuracy.value }} {{ locationFormatted.elevationUnits.value.name }}
                </span>
                <span text-sm text-neutral-400 v-if="locationFormatted.elevationSource.value">
                  {{ locationFormatted.elevationSource.value }}
                </span>
              </p>
            </template>
          </div>
        </slot>
        <!-- pages with a custom elevation slot place the source line themselves -->
        <ElevationSource
          v-if="showElevationSource && !$slots.elevation"
          :sources="elevationSources"
          :pending="elevationSourcesPending"
          :elevationUnits="locationFormatted.elevationUnits.value"
        />
      </div>
      <LocationLookup v-model="selectedLocation" />
      <Ad mb-4 ad-slot="6966721074" />
      <div space-y-4 max-w-lg>
        <div flex flex-wrap gap-2 gap-y-4 mb-2 justify-items-start>
          <slot name="settings"></slot>
          <Toggle :options="elevationUnitOptions" v-model="locationFormatted.unitSettings.value.elevationUnits" />
          <slot name="settings-bottom"></slot>
        </div>
        <Ad ad-slot="1948746426" />
      </div>
      <slot name="above-info"></slot>
      <div space-y-4 max-w-lg>
        <h2 text-xl sm:text-2xl font-semibold text-stone-500>More Information</h2>
        <div grid grid-flow-dense grid-cols-2 gap-2>
          <DataItem
            v-if="currentLocationSupported"
            :loading="loading"
            label="Speed"
            :value="locationFormatted.speed.value"
          />
          <DataItem
            v-if="currentLocationSupported"
            label="Direction"
            :loading="loading"
            :value="locationFormatted.direction.value"
          />
          <DataItem
            v-if="location.value?.floorLevel && currentLocationSupported"
            label="Floor Level"
            :value="location.value.floorLevel"
          />
          <DataItem
            :loading="loading"
            label="Boiling Point"
            :value="locationFormatted.boilingPoint.value?.value"
            :units="locationFormatted.boilingPoint.value?.units"
            class="col-span-2 sm:col-span-1"
          />
          <DataItem
            :loading="loading"
            label="Percent of the way up Mt. Everest"
            :value="locationFormatted.percentOfMtEverest.value"
            class="col-span-2 sm:col-span-1"
          />
          <DataItem
            :loading="loading || !locationFormatted?.coordinates.value"
            label="Coordinates"
            :value="locationFormatted.coordinates.value"
            class="col-span-2"
          />
        </div>
      </div>
      <Ad ad-slot="3658187561" />
      <slot name="below-info"></slot>
      <div class="prose lg:text-lg">
        <ElevationDataSection
          v-if="showElevationSource"
          :sources="elevationSources"
          :placeName="placeName"
          :altitude="location.altitude"
          :elevationUnits="locationFormatted.elevationUnits.value"
        />
        <h2>{{ page?.subtitle || "What is this?" }}</h2>
        <ContentRenderer v-if="page" :value="page" />
      </div>
    </div>
  </div>
</template>
<script setup>
import { useVModel } from "@vueuse/core";

const props = defineProps({
  loading: {
    type: Boolean,
    default: true,
  },
  location: Object,
  // why the device location couldn't be found, from pages whose `error` is something else (the map)
  locationError: Object,
  currentLocationSupported: Boolean,
  selectedLocation: Object,
  error: Object,
  // "none" or "failed" when the web elevation lookup finished without a value
  elevationStatus: String,
  path: {
    type: String,
    default: "/elevation",
  },
});
const emit = defineEmits(["update:selectedLocation"]);
const { currentLocationSupported, location, error } = toRefs(props);
const selectedLocation = useVModel(props, "selectedLocation", emit);

const locationFormatted = useLocationFormatted(location, error);
// why the device location couldn't be found: the `error` prop on pages showing the current location, or
// `locationError` from pages that pass it separately (e.g. the map, whose `error` is the elevation fetch)
const locationError = computed(() => {
  if (props.locationError) {
    return props.locationError;
  }
  return currentLocationSupported.value && isGeolocationError(error) ? error.value : null;
});

const isWebLocation = computed(
  () =>
    location.value?.altitudeSource === "web" &&
    isNumber(location.value?.latitude) &&
    isNumber(location.value?.longitude),
);
const showElevationSource = computed(() => isWebLocation.value && locationFormatted.elevation.value);

// fetched here so the source line and the article section share one request
const { data: elevationSources, pending: elevationSourcesPending } = useFetchElevationCoverage(() =>
  isWebLocation.value ? location.value : null,
);

const ELEVATION_MESSAGES = {
  none: "No elevation data is available for this location.",
  failed: "Couldn't load the elevation for this location. Please try again.",
};
const elevationMessage = computed(() => ELEVATION_MESSAGES[props.elevationStatus] || null);

const GENERIC_PLACE_NAMES = ["Current Location", "Map Location", "Saved Location"];
const placeName = computed(() => {
  const name = location.value?.name || selectedLocation.value?.name;
  return GENERIC_PLACE_NAMES.includes(name) ? null : name;
});

const elevationUnitOptions = [
  { value: "ft", label: "ft" },
  { value: "m", label: "m" },
];

watchEffect(() => {
  const newSpeedUnits = locationFormatted.unitSettings.value.elevationUnits == "ft" ? "mph" : "kmh";
  locationFormatted.unitSettings.value.speedUnits = newSpeedUnits;
});

const page = await useContent(props.path);
</script>
